import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress, Fade, LinearProgress, TextField, Select, MenuItem } from "@mui/material";
import {
  getBrokerOrderBook,
  getBrokerOrderBookColumns,
} from "../../api/brokerBooks";
import {
  getBulkOrders,
  getBulkOrderColumns,
  uploadFile,
  sampleFile,
  modifyBulkOrders,
  buildBulkModifyOrderItem,
  placeBulkOrders,
  deleteBulkOrders,
} from "../../api/bulkOrder";
import { modifyOrders as modifyGatewayOrders, cancelOrders } from "../../api/orderModification";
import { repeatOrders } from "../../api/repeatOrder";
import CustomTable from "../../components/CustomTable/CustomTable";
import BookRefreshBar from "../../components/BookRefreshBar/BookRefreshBar";
import BookTableColumnPrefsInfo from "../../components/BookTableColumnPrefsInfo/BookTableColumnPrefsInfo";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { extractColumnsConfig, resolveBookColumns } from "../../utils/brokerBookColumns";
import { TABLE_COLUMN_TABLE_IDS } from "../../utils/tableColumnPreferences";
import { useTableColumnPreferencesVersion } from "../../hooks/useTableColumnPreferences";
import styles from "./OrderBookPage.module.css";
import { MemoOrderBookModifyBulkFormRow, MemoOrderBookInitiateBulkFormRow } from "./OrderBookBulkFormModalRows";
import { orderStatusNavigationStateFromResult } from "../../utils/normalizeOrderStatusResponse";

const getErrorMessage = (err) => err?.response?.data?.detail || err.message || "An error occurred";

const ORDER_BOOK_PRIMARY_TAB_QUERY = "tab";

const ORDER_BOOK_PRIMARY_TAB_VALUES = new Set(["order-book", "bulk-order"]);

/** Read primary tab from `?tab=order-book` | `?tab=bulk-order`. */
function readOrderBookPrimaryTabFromSearchParams(searchParams) {
  const raw = searchParams.get(ORDER_BOOK_PRIMARY_TAB_QUERY);
  if (raw === "bulk-order" || raw === "order-book") return raw;
  return null;
}

/** Apply-to-all numeric fields: propagate to rows after user stops typing (ms). */
const BULK_APPLY_DEBOUNCE_MS = 450;

/** Bulk upload/modify API may return row-level issues in `error_rows` (HTTP 200 or 4xx body). */
function normalizeBulkOperationResponse(data) {
  if (!data || typeof data !== "object") {
    return { errorRows: [], successCount: null };
  }
  const raw = data.error_rows;
  const errorRows = Array.isArray(raw) ? raw : [];
  const sc = data.success_count;
  let successCount = null;
  if (sc != null && sc !== "") {
    const n = Number(sc);
    successCount = Number.isFinite(n) ? n : null;
  }
  /* Modify (and some upload) responses omit `success_count` but return updated rows in `bulk_orders`. */
  if (successCount == null && Array.isArray(data.bulk_orders) && data.bulk_orders.length > 0) {
    successCount = data.bulk_orders.length;
  }
  return { errorRows, successCount };
}

/** Axios error bodies are sometimes objects or JSON strings (proxies / gateways). */
function parseAxiosBulkOrderErrorBody(err) {
  const d = err?.response?.data;
  if (d == null) return null;
  if (typeof d === "object") return d;
  if (typeof d === "string") {
    try {
      const parsed = JSON.parse(d);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Human message from bulk-order JSON body when HTTP status is 4xx and there are no `error_rows`. */
function bulkOrderHttpErrorMessage(data, status) {
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  if (Array.isArray(detail)) {
    const joined = detail
      .map((x) => (typeof x === "object" && x?.msg ? x.msg : typeof x === "string" ? x : JSON.stringify(x)))
      .filter(Boolean)
      .join("; ");
    if (joined) return joined;
  }
  if (data?.message && typeof data.message === "string") return data.message;
  return `Request failed (${status})`;
}

function formatBulkApiCell(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "number" && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : String(v);
  }
  return String(v);
}

function BulkApiRowErrorsPanel({ feedback, onDismiss }) {
  const { errorRows, successCount } = feedback || {};
  if (!errorRows?.length) return null;
  const successLabel =
    successCount != null
      ? `${successCount} row${successCount === 1 ? "" : "s"} processed successfully`
      : null;
  return (
    <div className={styles["bulk-api-errors-wrap"]}>
      <div className={styles["bulk-api-errors-card"]} role="alert">
        <div className={styles["bulk-api-errors-header"]}>
          <div className={styles["bulk-api-errors-headerLead"]}>
            <div className={styles["bulk-api-errors-iconRing"]} aria-hidden>
              <GlobalIcon iconName="ModificationBookIcon" className={styles["bulk-api-errors-icon"]} />
            </div>
            <div className={styles["bulk-api-errors-headerCopy"]}>
              <p className={styles["bulk-api-errors-kicker"]}>Row issues</p>
              <h3 className={styles["bulk-api-errors-headline"]}>Some rows could not be applied</h3>
              <p className={styles["bulk-api-errors-lead"]}>
                Fix the problems below in your spreadsheet or in the form, then try again.
              </p>
              {successLabel ? (
                <div className={styles["bulk-api-errors-badges"]}>
                  <span className={styles["bulk-api-errors-pill"]}>{successLabel}</span>
                  <span className={styles["bulk-api-errors-pillIssue"]}>
                    {errorRows.length} issue{errorRows.length === 1 ? "" : "s"}
                  </span>
                </div>
              ) : (
                <div className={styles["bulk-api-errors-badges"]}>
                  <span className={styles["bulk-api-errors-pillIssue"]}>
                    {errorRows.length} issue{errorRows.length === 1 ? "" : "s"}
                  </span>
                </div>
              )}
            </div>
          </div>
          {typeof onDismiss === "function" ? (
            <Button
              type="button"
              size="small"
              variant="outlined"
              className={styles["bulk-api-errors-dismiss"]}
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          ) : null}
        </div>

        <div className={styles["bulk-api-errors-tableShell"]}>
          <TableContainer
            className={styles["bulk-api-errors-table-scroll"]}
            sx={{ maxHeight: "none", overflowY: "visible", overflowX: "auto" }}
          >
            <Table size="small" stickyHeader className={styles["bulk-api-errors-table"]}>
              <TableHead>
                <TableRow className={styles["bulk-api-errors-headRow"]}>
                  <TableCell className={styles["bulk-api-errors-th"]}>Row</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Client</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Symbol</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Trans</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Type</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Price</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Status</TableCell>
                  <TableCell className={styles["bulk-api-errors-th"]}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorRows.map((row, idx) => (
                  <TableRow key={`bulk-err-${idx}`} className={styles["bulk-api-errors-dataRow"]}>
                    <TableCell className={`${styles["bulk-api-errors-td"]} ${styles["bulk-api-errors-tdRow"]}`}>
                      {formatBulkApiCell(row.row)}
                    </TableCell>
                    <TableCell className={styles["bulk-api-errors-td"]}>{formatBulkApiCell(row.broker_client_code)}</TableCell>
                    <TableCell className={`${styles["bulk-api-errors-td"]} ${styles["bulk-api-errors-tdSymbol"]}`}>
                      {formatBulkApiCell(row.exchange_trading_symbol)}
                    </TableCell>
                    <TableCell className={styles["bulk-api-errors-td"]}>{formatBulkApiCell(row.transaction_type)}</TableCell>
                    <TableCell className={styles["bulk-api-errors-td"]}>{formatBulkApiCell(row.order_type)}</TableCell>
                    <TableCell className={styles["bulk-api-errors-td"]}>{formatBulkApiCell(row.order_price)}</TableCell>
                    <TableCell className={`${styles["bulk-api-errors-td"]} ${styles["bulk-api-errors-tdStatus"]}`}>
                      {formatBulkApiCell(row.status)}
                    </TableCell>
                    <TableCell className={`${styles["bulk-api-errors-td"]} ${styles["bulk-api-errors-tdReason"]}`}>
                      {formatBulkApiCell(row.reason)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

/** Shown after bulk modify when the gateway returns `error_rows: []` and optional `bulk_orders` (same contract as upload). */
function BulkApiBulkModifySuccessPanel({ updatedCount }) {
  const n = Number(updatedCount);
  const label = Number.isFinite(n) && n >= 0 ? n : 0;
  return (
    <div className={styles["bulk-api-success-wrap"]}>
      <div className={styles["bulk-api-success-card"]} role="status">
        <div className={styles["bulk-api-success-inner"]}>
          <div className={styles["bulk-api-success-iconRing"]} aria-hidden>
            <GlobalIcon iconName="VerifiedIcon" className={styles["bulk-api-success-icon"]} />
          </div>
          <div className={styles["bulk-api-success-copy"]}>
            <p className={styles["bulk-api-success-kicker"]}>Bulk orders updated</p>
            <h3 className={styles["bulk-api-success-headline"]}>
              {label === 0 ? "Changes were saved" : `${label} order${label === 1 ? "" : "s"} updated`}
            </h3>
            <p className={styles["bulk-api-success-lead"]}>
              No issues found. The table has been refreshed. Press <strong>Done</strong> to close.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCancelModalOrderQty(row) {
  const raw = row.order_quantity ?? row.order_lot ?? row.quantity;
  if (raw === "" || raw == null) return "—";
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw);
  return Number.isInteger(n) ? String(n) : String(n);
}

function formatCancelModalOrderPrice(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

/** Milliseconds for sorting; latest first. Tries common API timestamp fields. */
function rowTimestampMs(row) {
  if (!row || typeof row !== "object") return 0;
  const candidates = [
    row.order_timestamp,
    row.trade_timestamp,
    row.timestamp,
    row.created_at,
    row.updated_at,
    row.placed_at,
    row.order_time,
  ];
  for (const v of candidates) {
    if (v == null || v === "") continue;
    if (typeof v === "number" && !Number.isNaN(v)) {
      return v > 1e12 ? v : v * 1000;
    }
    const s = String(v).trim();
    const parsed = Date.parse(s.includes("T") ? s : s.replace(" ", "T"));
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
}

function sortBookRowsLatestFirst(rows) {
  if (!Array.isArray(rows)) return rows;
  return [...rows].sort((a, b) => {
    const tb = rowTimestampMs(b);
    const ta = rowTimestampMs(a);
    if (tb !== ta) return tb - ta;
    const nb = Number(b.bulk_order_id ?? b.broker_order_id);
    const na = Number(a.bulk_order_id ?? a.broker_order_id);
    if (!Number.isNaN(nb) && !Number.isNaN(na) && nb !== na) return nb - na;
    return 0;
  });
}

function sortBulkOrderResponse(data) {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  for (const key of ["fresh_bulk", "bulk_orders"]) {
    if (Array.isArray(next[key])) {
      next[key] = sortBookRowsLatestFirst(next[key]);
    }
  }
  return next;
}

const ORDER_BOOK_TABS = [
  { label: "Open Orders", value: "open" },
  { label: "Completed Orders", value: "completed" },
  { label: "Cancel Orders", value: "cancel" },
  { label: "Rejected Orders", value: "rejected" }
];

const ORDER_BOOK_TAB_TO_API_STATUS = {
  open: "OPEN",
  completed: "COMPLETED",
  cancel: "CANCELLED",
  rejected: "REJECTED",
};

function getApiStatusForOrderBookTab(tab) {
  return ORDER_BOOK_TAB_TO_API_STATUS[tab] ?? "OPEN";
}

const MODIFY_SELECT_MENU_PROPS = {
  slotProps: {
    paper: {
      sx: {
        "& .MuiMenuItem-root": { minHeight: 26, fontSize: 9, py: 0.25, px: 1.25 },
      },
    },
  },
};

/** Disclosed qty derived from total qty; NFO always uses 0. */
function defaultDisclosedForExchangeQty(exchange, qty) {
  if (exchange === "NFO") return 0;
  return Math.round(qty * 0.1);
}

/** Keep slice value consistent with qty when quantity changes (e.g. apply-to-all lot/qty). */
function syncInitiateSliceWithQty(row, newQty) {
  const q = newQty;
  const type = row.order_slice_type ?? "ABSOLUTE";
  if (type === "PERCENTAGE") {
    const prev = Number(row.order_slice_value);
    if (Number.isNaN(prev)) return 100;
    return Math.min(100, Math.max(0, prev));
  }
  const prev = Number(row.order_slice_value);
  if (Number.isNaN(prev)) return q;
  return Math.min(q, Math.max(0, prev));
}

function applyInitiateFieldToRow(row, field, value) {
  if (
    field === "product_type" &&
    value === "MARGIN" &&
    (row.exchange === "NFO" || row.exchange === "BFO")
  ) {
    return row;
  }
  const next = { ...row };
  const qtyRaw = next.order_quantity ?? next.order_lot;
  const qty = qtyRaw === "" || qtyRaw == null ? 0 : Number(qtyRaw) || 0;
  let finalValue = value;

  if (field === "order_price" || field === "trigger_price") {
    finalValue = value === "" || value == null ? "" : value;
  } else if (field === "time_interval") {
    finalValue = value === "" || value == null ? "" : Number(value) || 0;
  } else if (field === "order_slice_value") {
    if (value === "" || value == null) {
      finalValue = "";
    } else {
    const type = next.order_slice_type ?? "ABSOLUTE";
    const num = Number(value);
    if (!Number.isNaN(num)) {
      if (type === "PERCENTAGE") {
        if (num > 100) finalValue = 100;
        else if (num < 0) finalValue = 0;
        else finalValue = num;
      } else if (num > qty) finalValue = qty;
      else if (num < 0) finalValue = 0;
      else finalValue = num;
      } else {
        finalValue = value;
      }
    }
  } else if (field === "disclosed_quantity") {
    if (value === "" || value == null) {
      finalValue = "";
    } else {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      if (num > qty) finalValue = qty;
      else if (num < 0) finalValue = 0;
      else finalValue = num;
      } else {
        finalValue = value;
      }
    }
  }

  next[field] = finalValue;

  if (field === "order_slice_type" && value === "PERCENTAGE") {
    const num = Number(next.order_slice_value);
    if (!Number.isNaN(num) && num > 100) next.order_slice_value = 100;
    else if (!Number.isNaN(num) && num < 0) next.order_slice_value = 0;
  }
  if (field === "order_slice_type" && value === "ABSOLUTE") {
    const num = Number(next.order_slice_value);
    const q = Number(next.order_quantity ?? next.order_lot) || 0;
    if (!Number.isNaN(num) && num > q) next.order_slice_value = q;
    else if (!Number.isNaN(num) && num < 0) next.order_slice_value = 0;
  }
  if (next.exchange === "NFO") {
    next.disclosed_quantity = 0;
  }
  return next;
}

function OrderBookPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [primaryTab, setPrimaryTab] = useState(() => {
    try {
      const p = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
      const t = readOrderBookPrimaryTabFromSearchParams(p);
      if (t) return t;
    } catch {
      /* ignore */
    }
    return "order-book";
  });
  const [allOrderBook, setAllOrderBook] = useState([]);

  useEffect(() => {
    const raw = searchParams.get(ORDER_BOOK_PRIMARY_TAB_QUERY);
    if (raw != null && !ORDER_BOOK_PRIMARY_TAB_VALUES.has(raw)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(ORDER_BOOK_PRIMARY_TAB_QUERY);
          return next;
        },
        { replace: true }
      );
      setPrimaryTab("order-book");
      return;
    }
    const nextTab = readOrderBookPrimaryTabFromSearchParams(searchParams) ?? "order-book";
    setPrimaryTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const tabFromState = location.state?.primaryTab;
    if (!tabFromState || !ORDER_BOOK_PRIMARY_TAB_VALUES.has(tabFromState)) return;
    setPrimaryTab(tabFromState);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tabFromState === "order-book") next.delete(ORDER_BOOK_PRIMARY_TAB_QUERY);
        else next.set(ORDER_BOOK_PRIMARY_TAB_QUERY, tabFromState);
        return next;
      },
      { replace: true }
    );
  }, [location.state?.primaryTab, setSearchParams]);
  const [orderApiColumns, setOrderApiColumns] = useState([]);
  useTableColumnPreferencesVersion(TABLE_COLUMN_TABLE_IDS.ORDER_BOOK);
  const orderColumnsConfig = resolveBookColumns(orderApiColumns, TABLE_COLUMN_TABLE_IDS.ORDER_BOOK);

  const [bulkOrderData, setBulkOrderData] = useState({});
  const [bulkOrderApiColumns, setBulkOrderApiColumns] = useState([]);
  useTableColumnPreferencesVersion(TABLE_COLUMN_TABLE_IDS.BULK_ORDER);
  const bulkOrderColumnsConfig = resolveBookColumns(bulkOrderApiColumns, TABLE_COLUMN_TABLE_IDS.BULK_ORDER);

  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("open");
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadModalFile, setUploadModalFile] = useState(null);
  const [uploadModalError, setUploadModalError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  /** Row-level API feedback from bulk upload or bulk modify (`error_rows` + `success_count`). */
  const [bulkApiRowFeedback, setBulkApiRowFeedback] = useState(null);
  /** Bulk modify OK response with empty `error_rows` (gateway may return `bulk_orders` only). */
  const [bulkModifyApiSuccessFeedback, setBulkModifyApiSuccessFeedback] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInitiateModalOpen, setIsInitiateModalOpen] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [modifyFormRows, setModifyFormRows] = useState([]);
  const [isModifying, setIsModifying] = useState(false);
  const [modifyModalSource, setModifyModalSource] = useState(null);
  const [modifyModalError, setModifyModalError] = useState("");
  const [isOrderBookCancelModalOpen, setIsOrderBookCancelModalOpen] = useState(false);
  const [isCancellingOrders, setIsCancellingOrders] = useState(false);
  const [orderBookCancelError, setOrderBookCancelError] = useState("");
  const [isOrderBookRepeatModalOpen, setIsOrderBookRepeatModalOpen] = useState(false);
  const [isRepeatingOrders, setIsRepeatingOrders] = useState(false);
  const [orderBookRepeatError, setOrderBookRepeatError] = useState("");
  const [initiateFormRows, setInitiateFormRows] = useState([]);
  const [modifyBulkRowKey, setModifyBulkRowKey] = useState(0);
  const [initiateBulkRowKey, setInitiateBulkRowKey] = useState(0);
  const [lastOrderBookRefreshAt, setLastOrderBookRefreshAt] = useState(null);
  const [lastBulkRefreshAt, setLastBulkRefreshAt] = useState(null);
  const uploadModalInputRef = useRef(null);
  const bulkApplyTimersRef = useRef({});
  /** Ignore stale order-book responses if the user switches tabs before the request finishes */
  const orderBookFetchGenRef = useRef(0);

  const scheduleBulkApplyTimer = useCallback((key, run) => {
    const t = bulkApplyTimersRef.current;
    if (t[key]) clearTimeout(t[key]);
    t[key] = setTimeout(() => {
      t[key] = null;
      run();
    }, BULK_APPLY_DEBOUNCE_MS);
  }, []);

  const cancelBulkApplyTimer = useCallback((key) => {
    const t = bulkApplyTimersRef.current;
    if (t[key]) {
      clearTimeout(t[key]);
      t[key] = null;
    }
  }, []);

  useEffect(() => {
    if (!isModifyModalOpen && !isInitiateModalOpen) {
      const t = bulkApplyTimersRef.current;
      Object.keys(t).forEach((k) => {
        if (t[k]) clearTimeout(t[k]);
        delete t[k];
      });
    }
  }, [isModifyModalOpen, isInitiateModalOpen]);

  useEffect(() => {
    if (isModifyModalOpen) setModifyBulkRowKey((k) => k + 1);
  }, [isModifyModalOpen]);

  useEffect(() => {
    if (isInitiateModalOpen) setInitiateBulkRowKey((k) => k + 1);
  }, [isInitiateModalOpen]);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const response = await getBrokerOrderBookColumns();
        const columns = extractColumnsConfig(response);
        if (columns?.length > 0) {
          setOrderApiColumns(columns);
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      }
    };

    fetchColumns();
  }, []);

  useEffect(() => {
    if (primaryTab !== "bulk-order" || bulkOrderApiColumns.length > 0) return;

    const fetchBulkColumns = async () => {
      try {
        const response = await getBulkOrderColumns();
        const columns = extractColumnsConfig(response);
        if (columns?.length > 0) {
          setBulkOrderApiColumns(columns);
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      }
    };

    fetchBulkColumns();
  }, [primaryTab, bulkOrderApiColumns.length]);

  const fetchOrderBook = useCallback(async () => {
    const gen = ++orderBookFetchGenRef.current;
    setIsLoading(true);
    setError("");

    try {
      const status = getApiStatusForOrderBookTab(activeTab);
      const data = await getBrokerOrderBook(status);
      if (orderBookFetchGenRef.current !== gen) return;
      setAllOrderBook(sortBookRowsLatestFirst(data["book_rows"] || []));
      setLastOrderBookRefreshAt(Date.now());
    } catch (err) {
      if (orderBookFetchGenRef.current !== gen) return;
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setAllOrderBook([]);
    } finally {
      if (orderBookFetchGenRef.current === gen) {
        setIsLoading(false);
      }
    }
  }, [activeTab]);

  const fetchBulkOrders = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getBulkOrders();
      setBulkOrderData(sortBulkOrderResponse(resp || {}));
      setLastBulkRefreshAt(Date.now());
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setBulkOrderData({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (primaryTab !== "order-book") return;
    void fetchOrderBook();
  }, [primaryTab, activeTab, fetchOrderBook]);

  useEffect(() => {
    if (primaryTab !== "bulk-order") return;
    void fetchBulkOrders();
  }, [primaryTab, fetchBulkOrders]);

  const handleBookToolbarReload = useCallback(() => {
    if (primaryTab === "order-book") void fetchOrderBook();
    else if (primaryTab === "bulk-order") void fetchBulkOrders();
  }, [primaryTab, fetchOrderBook, fetchBulkOrders]);

  const bookLastRefreshAt =
    primaryTab === "order-book"
      ? lastOrderBookRefreshAt
      : primaryTab === "bulk-order"
        ? lastBulkRefreshAt
        : null;

  const showBlockingTableLoad =
    isLoading &&
    (primaryTab === "order-book"
      ? lastOrderBookRefreshAt === null
      : primaryTab === "bulk-order"
        ? lastBulkRefreshAt === null
        : false);

  const orderBookCancelCandidates = useMemo(() => {
    if (primaryTab !== "order-book" || activeTab !== "open") return [];
    const sel = new Set(selectedRows);
    return allOrderBook.filter(
      (row) =>
        sel.has(row.key) &&
        row.status === "OPEN" &&
        row.broker_order_id &&
        row.broker_client_code
    );
  }, [primaryTab, activeTab, selectedRows, allOrderBook]);

  const orderBookRepeatCandidates = useMemo(() => {
    if (primaryTab !== "order-book") return [];
    const sel = new Set(selectedRows);
    return allOrderBook.filter(
      (row) => sel.has(row.key) && row.broker_order_id && row.broker_client_code
    );
  }, [primaryTab, selectedRows, allOrderBook]);

  const handleRowSelect = useCallback((selectedIds, selectedRowsData) => {
    setSelectedRows(selectedIds);
  }, []);

  const handlePrimaryTabChange = useCallback(
    (event, newValue) => {
      setPrimaryTab(newValue);
      setSelectedRows([]);
      setError("");
      setBulkApiRowFeedback(null);
      setBulkModifyApiSuccessFeedback(null);
      setIsModifyModalOpen(false);
      setModifyModalSource(null);
      setModifyModalError("");
      setIsOrderBookCancelModalOpen(false);
      setOrderBookCancelError("");
      setIsOrderBookRepeatModalOpen(false);
      setOrderBookRepeatError("");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newValue === "order-book") next.delete(ORDER_BOOK_PRIMARY_TAB_QUERY);
          else next.set(ORDER_BOOK_PRIMARY_TAB_QUERY, newValue);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleModify = () => {
    if (selectedRows.length === 0) return;
    if (primaryTab === "bulk-order") {
      setModifyModalError("");
      setBulkApiRowFeedback(null);
      setBulkModifyApiSuccessFeedback(null);
      setModifyModalSource("bulk");
      setIsModifyModalOpen(true);
    } else if (primaryTab === "order-book" && activeTab === "open") {
      setModifyModalError("");
      setBulkModifyApiSuccessFeedback(null);
      setModifyModalSource("order-book");
      setIsModifyModalOpen(true);
    }
  };

  const closeModifyModal = useCallback(() => {
    if (!isModifying) {
      setIsModifyModalOpen(false);
      setModifyModalSource(null);
      setModifyModalError("");
      setBulkApiRowFeedback(null);
      setBulkModifyApiSuccessFeedback(null);
    }
  }, [isModifying]);

  const dismissBulkModifySuccess = useCallback(() => {
    setBulkModifyApiSuccessFeedback(null);
    setIsModifyModalOpen(false);
    setModifyModalSource(null);
    setSelectedRows([]);
  }, []);

  const handleRepeatClick = useCallback(() => {
    if (primaryTab !== "order-book" || selectedRows.length === 0) return;
    setOrderBookRepeatError("");
    setIsOrderBookRepeatModalOpen(true);
  }, [primaryTab, selectedRows.length]);

  const handleOrderBookRepeatModalClose = useCallback(() => {
    if (!isRepeatingOrders) {
      setIsOrderBookRepeatModalOpen(false);
      setOrderBookRepeatError("");
    }
  }, [isRepeatingOrders]);

  const handleConfirmOrderBookRepeat = useCallback(async () => {
    const order_list = orderBookRepeatCandidates.map(({ broker_order_id, broker_client_code }) => ({
      broker_order_id,
      broker_client_code,
    }));
    if (order_list.length === 0) {
      setOrderBookRepeatError("Each selected row needs a broker order id and client code.");
      return;
    }
    setIsRepeatingOrders(true);
    setOrderBookRepeatError("");
    setError("");
    try {
      const response = await repeatOrders(order_list);
      if (response?.status === true) {
        setIsOrderBookRepeatModalOpen(false);
        setOrderBookRepeatError("");
        setSelectedRows([]);
        void fetchOrderBook();
        navigate("/order-status", { state: orderStatusNavigationStateFromResult(response) });
      } else {
        setOrderBookRepeatError(response?.detail || "Repeat order failed.");
      }
    } catch (err) {
      setOrderBookRepeatError(getErrorMessage(err));
    } finally {
      setIsRepeatingOrders(false);
    }
  }, [orderBookRepeatCandidates, fetchOrderBook, navigate]);

  const handleCancelClick = useCallback(() => {
    if (selectedRows.length === 0) return;
    setOrderBookCancelError("");
    setIsOrderBookCancelModalOpen(true);
  }, [selectedRows.length]);

  const handleOrderBookCancelModalClose = useCallback(() => {
    if (!isCancellingOrders) {
      setIsOrderBookCancelModalOpen(false);
      setOrderBookCancelError("");
    }
  }, [isCancellingOrders]);

  const handleConfirmOrderBookCancel = useCallback(async () => {
    const order_list = orderBookCancelCandidates.map(({ broker_order_id, broker_client_code }) => ({
      broker_order_id,
      broker_client_code,
    }));
    if (order_list.length === 0) {
      setOrderBookCancelError("No orders can be cancelled. Each row must be open and include a broker order id and client code.");
      return;
    }
    setIsCancellingOrders(true);
    setOrderBookCancelError("");
    setError("");
    try {
      const response = await cancelOrders(order_list);
      if (response?.status === true) {
        setIsOrderBookCancelModalOpen(false);
        setOrderBookCancelError("");
        setSelectedRows([]);
        await fetchOrderBook();
      } else {
        setOrderBookCancelError(response?.detail || "Cancellation failed.");
      }
    } catch (err) {
      setOrderBookCancelError(getErrorMessage(err));
    } finally {
      setIsCancellingOrders(false);
    }
  }, [orderBookCancelCandidates, fetchOrderBook]);

  const handleInitiate = () => {
    if (primaryTab === "bulk-order" && selectedRows.length > 0) {
      setError("");
      setIsInitiateModalOpen(true);
    }
  };

  const handleDeleteClick = () => {
    if (selectedRows.length > 0) setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedRows.length === 0) return;
    setIsDeleting(true);
    setError("");
    try {
      await deleteBulkOrders(selectedRows);
      const resp = await getBulkOrders();
      setBulkOrderData(sortBulkOrderResponse(resp || {}));
      setIsDeleteModalOpen(false);
      setSelectedRows([]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRows]);

  const handleDeleteModalClose = useCallback(() => {
    if (!isDeleting) setIsDeleteModalOpen(false);
  }, [isDeleting]);

  const handleUploadFileClick = () => {
    setUploadModalFile(null);
    setUploadModalError("");
    setBulkApiRowFeedback(null);
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = useCallback(() => {
    if (!isUploading) {
      setIsUploadModalOpen(false);
      setUploadModalFile(null);
      setUploadModalError("");
      setBulkApiRowFeedback(null);
    }
  }, [isUploading]);

  const handleUploadModalFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadModalFile(file);
      setUploadModalError("");
    }
    e.target.value = "";
  }, []);

  const handleUploadModalDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && /\.(xlsx|xls|csv)$/i.test(file.name)) {
      setUploadModalFile(file);
      setUploadModalError("");
    }
  }, []);

  const handleUploadModalDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDownloadSample = useCallback(async () => {
    setIsDownloadingSample(true);
    try {
      await sampleFile();
    } finally {
      setIsDownloadingSample(false);
    }
  }, []);

  const handleUploadSubmit = useCallback(async () => {
    if (!uploadModalFile) return;
    setIsUploading(true);
    setBulkApiRowFeedback(null);
    setUploadModalError("");
    setError("");
    const applyUploadResponse = async (data, errBody) => {
      const payload = data ?? errBody;
      const { errorRows, successCount } = normalizeBulkOperationResponse(payload);
      try {
        const resp = await getBulkOrders();
        setBulkOrderData(sortBulkOrderResponse(resp || {}));
        setLastBulkRefreshAt(Date.now());
      } catch (refreshErr) {
        setUploadModalError(getErrorMessage(refreshErr));
      }
      if (errorRows.length > 0) {
        setBulkApiRowFeedback({ source: "upload", errorRows, successCount });
        return true;
      }
      return false;
    };
    try {
      const formData = new FormData();
      formData.append("bulk_order_file", uploadModalFile);
      const res = await uploadFile(formData);
      const data = res.data;
      const status = res.status;
      const hasRowErrors = await applyUploadResponse(data, null);
      if (hasRowErrors) {
        setUploadModalError("");
        setError("");
        setIsUploadModalOpen(true);
      } else if (status >= 400) {
        setUploadModalError(bulkOrderHttpErrorMessage(data, status));
        setError("");
        setIsUploadModalOpen(true);
      } else {
        setIsUploadModalOpen(false);
        setUploadModalFile(null);
        setUploadModalError("");
        setBulkApiRowFeedback(null);
      }
    } catch (err) {
      const body = parseAxiosBulkOrderErrorBody(err);
      const hadRowErrors = body && (await applyUploadResponse(null, body));
      if (hadRowErrors) {
        setUploadModalError("");
        setError("");
        setIsUploadModalOpen(true);
      } else {
        const status = err?.response?.status;
        const msg =
          body && status != null
            ? bulkOrderHttpErrorMessage(body, status)
            : getErrorMessage(err);
        setUploadModalError(msg);
        setError("");
        setIsUploadModalOpen(true);
      }
    } finally {
      setIsUploading(false);
    }
  }, [uploadModalFile]);

  const handleExport = useCallback(() => {}, []);

  const currentTableData = useMemo(() => {
    if (primaryTab === "bulk-order") {
      return bulkOrderData["fresh_bulk"] || bulkOrderData["bulk_orders"] || [];
    }
    if (primaryTab === "order-book") {
      return allOrderBook;
    }
    return [];
  }, [primaryTab, bulkOrderData, allOrderBook]);

  const currentColumns = useMemo(() => {
    if (primaryTab === "bulk-order") {
      return bulkOrderColumnsConfig;
    }
    return orderColumnsConfig;
  }, [primaryTab, bulkOrderColumnsConfig, orderColumnsConfig]);

  const currentRowKey = useMemo(() => {
    return primaryTab === "bulk-order" ? "bulk_order_id" : "key";
  }, [primaryTab]);

  const secondaryTabsConfig = useMemo(() => {
    if (primaryTab === "order-book") return ORDER_BOOK_TABS;
    return null;
  }, [primaryTab]);

  const columnsWithDropdown = useMemo(() => {
    return primaryTab === "bulk-order"
      ? ['client_code', 'trading_symbol']
      : ['broker_client_code', 'exchange_trading_symbol'];
  }, [primaryTab]);

  const activeSecondaryTab = useMemo(() => {
    if (primaryTab === "bulk-order") return null;
    return activeTab;
  }, [primaryTab, activeTab]);

  const exportType = useMemo(() => {
    if (primaryTab === "order-book") {
      const tabToExportType = {
        "open": "OpenOrders",
        "completed": "CompletedOrders",
        "cancel": "CanceledOrders",
        "rejected": "RejectedOrders"
      };
      return tabToExportType[activeTab] || "Orders";
    }
    if (primaryTab === "bulk-order") {
      return "BulkOrders";
    }
    return null;
  }, [primaryTab, activeTab]);

  const handleSecondaryTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]);
  }, []);

  const selectedRowsData = useMemo(() => {
    if (selectedRows.length === 0) return [];
    if (primaryTab === "bulk-order") {
      const currentData = bulkOrderData["fresh_bulk"] || bulkOrderData["bulk_orders"] || [];
      return currentData.filter((row) => selectedRows.includes(row.bulk_order_id));
    }
    if (primaryTab === "order-book") {
      return allOrderBook.filter((row) => selectedRows.includes(row.key));
    }
    return [];
  }, [primaryTab, selectedRows, bulkOrderData, allOrderBook]);

  const buildOrderListForPlace = useCallback(() => {
    const source = initiateFormRows.length === selectedRowsData.length ? initiateFormRows : selectedRowsData;
    const rows = source.length ? source : selectedRowsData;
    return rows.map((row) => {
      const qtyRaw = row.order_quantity ?? row.order_lot;
      const qty = qtyRaw === "" || qtyRaw == null ? 0 : Number(qtyRaw) || 0;
      const numOr = (v, fallback) => {
        if (v === "" || v == null) return fallback;
        const n = Number(v);
        return Number.isNaN(n) ? fallback : n;
      };
      const sliceRaw = row.order_slice_value;
      const sliceNum =
        sliceRaw === "" || sliceRaw == null ? qty : Number.isNaN(Number(sliceRaw)) ? qty : Number(sliceRaw);
      const tiRaw = row.time_interval;
      const timeInterval =
        tiRaw === "" || tiRaw == null ? 120 : Number.isNaN(Number(tiRaw)) ? 120 : Number(tiRaw);
      return {
        bulk_order_id: row.bulk_order_id,
        broker_client_code: row.broker_client_code ?? "",
        exchange: row.exchange ?? "NSE",
        exchange_trading_symbol: row.exchange_trading_symbol ?? "",
        transaction_type: row.transaction_type ?? "BUY",
        order_type: row.order_type ?? "LIMIT",
        order_price: numOr(row.order_price, 0),
        trigger_price: numOr(row.trigger_price, 0),
        order_quantity: qty,
        disclosed_quantity:
          (row.exchange ?? "NSE") === "NFO" ? 0 : numOr(row.disclosed_quantity, 0),
        product_type: row.product_type ?? "DELIVERY",
        lot_size: Number(row.lot_size) || 1,
        order_slice_type: row.order_slice_type ?? "ABSOLUTE",
        order_slice_value: sliceNum,
        validity: row.validity ?? "IOC",
        sweep_order: row.sweep_order === true || row.sweep_order === "true",
        time_interval: timeInterval,
      };
    });
  }, [initiateFormRows, selectedRowsData]);

  const canPlaceOrders = useMemo(() => {
    if (selectedRowsData.length === 0 || initiateFormRows.length !== selectedRowsData.length) return false;
    return initiateFormRows.every((row) => {
      const v = Number(row.order_slice_value);
      const type = row.order_slice_type ?? "ABSOLUTE";
      const qty = Number(row.order_quantity ?? row.order_lot) || 0;
      const disclosed = Number(row.disclosed_quantity) || 0;
      if (Number.isNaN(v)) return false;
      if (v < 0) return false;
      if (qty < 0) return false;
      if (disclosed < 0) return false;
      if (disclosed > qty) return false;
      if (type === "PERCENTAGE" && v > 100) return false;
      if (type === "ABSOLUTE" && v > qty) return false;
      return true;
    });
  }, [initiateFormRows, selectedRowsData.length]);

  const handleConfirmInitiate = useCallback(async () => {
    const order_list = buildOrderListForPlace();
    if (order_list.length === 0) return;
    setIsPlacing(true);
    setError("");
    try {
      const result = await placeBulkOrders(order_list);
      await getBulkOrders().then((resp) => setBulkOrderData(sortBulkOrderResponse(resp || {})));
      setIsInitiateModalOpen(false);
      setSelectedRows([]);
      navigate("/order-status", { state: orderStatusNavigationStateFromResult(result) });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsPlacing(false);
    }
  }, [buildOrderListForPlace, navigate]);

  useEffect(() => {
    if (!isInitiateModalOpen || primaryTab !== "bulk-order" || selectedRowsData.length === 0) {
      if (!isInitiateModalOpen) setInitiateFormRows([]);
      return;
    }
    const rows = selectedRowsData.map((row) => {
      const qtyRaw = row.order_quantity ?? row.order_lot ?? 0;
      const qtyNum = Number(qtyRaw);
      const qty = Number.isNaN(qtyNum) ? 0 : qtyNum;
      const disclosedRaw = row.disclosed_quantity ?? row.disclosed_qty ?? 0;
      const disclosedNum = Number(disclosedRaw);
      const ex = row.exchange ?? "NSE";
      const disclosed =
        ex === "NFO" ? 0 : Number.isNaN(disclosedNum) ? 0 : disclosedNum;
      const rawOrderType = row.order_type ?? "LIMIT";
      const orderTypeForBulkUi = rawOrderType === "MARKET" ? "LIMIT" : rawOrderType;
      return {
        bulk_order_id: row.bulk_order_id,
        broker_client_code: row.broker_client_code ?? "",
        exchange: ex,
        exchange_trading_symbol: row.exchange_trading_symbol ?? "",
        transaction_type: row.transaction_type ?? "BUY",
        order_type: orderTypeForBulkUi,
        order_price: Number.isNaN(Number(row.order_price)) ? 0 : Number(row.order_price),
        trigger_price: Number.isNaN(Number(row.trigger_price)) ? 0 : Number(row.trigger_price),
        order_quantity: qty,
        disclosed_quantity: disclosed,
        product_type: row.product_type ?? "DELIVERY",
        lot_size: Number.isNaN(Number(row.lot_size)) || Number(row.lot_size) <= 0 ? 1 : Number(row.lot_size),
        order_slice_type: row.order_slice_type ?? "ABSOLUTE",
        order_slice_value: qty,
        validity: row.validity ?? "IOC",
        sweep_order: row.sweep_order ?? false,
        time_interval: Number.isNaN(Number(row.time_interval)) ? 120 : Number(row.time_interval),
      };
    });
    setInitiateFormRows(rows);
  }, [isInitiateModalOpen, primaryTab, selectedRowsData]);

  useEffect(() => {
    if (!isModifyModalOpen || !modifyModalSource || selectedRowsData.length === 0) {
      if (!isModifyModalOpen) {
        setModifyFormRows([]);
      }
      return;
    }
    if (modifyModalSource === "bulk") {
      const rows = selectedRowsData.map((row) => {
        const qtyRaw = row.order_quantity ?? row.order_lot ?? 0;
        const qty = Number(qtyRaw);
        const orderQuantity = Number.isNaN(qty) ? 0 : qty;
        const disclosedRaw = row.disclosed_quantity ?? row.disclosed_qty ?? 0;
        const disclosedNum = Number(disclosedRaw);
        const disclosedQuantity = Number.isNaN(disclosedNum) ? 0 : disclosedNum;
        const bulkEx = row.exchange ?? "NSE";
        const rawOt = row.order_type ?? "LIMIT";
        const orderTypeForBulkUi = rawOt === "MARKET" ? "LIMIT" : rawOt;
        return {
          bulk_order_id: row.bulk_order_id,
          broker_client_code: row.broker_client_code ?? "",
          exchange: bulkEx,
          exchange_trading_symbol: row.exchange_trading_symbol ?? "",
          transaction_type: row.transaction_type ?? "BUY",
          order_type: orderTypeForBulkUi,
          order_price: Number.isNaN(Number(row.order_price)) ? 0 : Number(row.order_price),
          trigger_price: Number.isNaN(Number(row.trigger_price)) ? 0 : Number(row.trigger_price),
          order_quantity: orderQuantity,
          disclosed_quantity: bulkEx === "NFO" ? 0 : disclosedQuantity,
          product_type: row.product_type ?? "DELIVERY",
          lot_size: Number.isNaN(Number(row.lot_size)) || Number(row.lot_size) <= 0 ? 1 : Number(row.lot_size),
          order_slice_type: row.order_slice_type ?? "PERCENTAGE",
          order_slice_value: Number.isNaN(Number(row.order_slice_value)) ? 100 : Number(row.order_slice_value),
          validity: row.validity ?? "IOC",
          sweep_order: row.sweep_order ?? true,
          time_interval: Number.isNaN(Number(row.time_interval)) ? 120 : Number(row.time_interval),
        };
      });
      setModifyFormRows(rows);
      return;
    }
    if (modifyModalSource === "order-book") {
      const rows = selectedRowsData.map((row) => {
        const qtyRaw = row.order_quantity ?? row.order_lot ?? 0;
        const qty = Number(qtyRaw);
        const orderQuantity = Number.isNaN(qty) ? 0 : qty;
        const disclosedRaw = row.disclosed_quantity ?? row.disclosed_qty ?? 0;
        const disclosedNum = Number(disclosedRaw);
        const obEx = row.exchange ?? "NSE";
        const disclosedQuantity =
          obEx === "NFO" ? 0 : Number.isNaN(disclosedNum) ? 0 : disclosedNum;
        const rawObOt = row.order_type ?? "LIMIT";
        const orderTypeForModifyUi = rawObOt === "MARKET" ? "LIMIT" : rawObOt;
        return {
          orderBookRowKey: row.key,
          broker_order_id: row.broker_order_id,
          broker_client_code: row.broker_client_code ?? "",
          exchange: obEx,
          exchange_trading_symbol: row.exchange_trading_symbol ?? "",
          transaction_type: row.transaction_type ?? "BUY",
          order_type: orderTypeForModifyUi,
          order_price: Number.isNaN(Number(row.order_price)) ? 0 : Number(row.order_price),
          trigger_price: Number.isNaN(Number(row.trigger_price)) ? 0 : Number(row.trigger_price),
          order_quantity: orderQuantity,
          disclosed_quantity: disclosedQuantity,
          product_type: row.product_type ?? "DELIVERY",
          lot_size: Number.isNaN(Number(row.lot_size)) || Number(row.lot_size) <= 0 ? 1 : Number(row.lot_size),
        };
      });
      setModifyFormRows(rows);
    }
  }, [isModifyModalOpen, modifyModalSource, selectedRowsData]);

  const handleInitiateFormChange = useCallback((rowIndex, field, value) => {
    setInitiateFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      next[rowIndex] = applyInitiateFieldToRow(next[rowIndex], field, value);
      return next;
    });
  }, []);

  const bumpModifyBulkRow = useCallback(() => setModifyBulkRowKey((k) => k + 1), []);
  const bumpInitiateBulkRow = useCallback(() => setInitiateBulkRowKey((k) => k + 1), []);

  const bulkApplyModifyField = useCallback(
    (field, rawValue) => {
      setModifyFormRows((prev) =>
        prev.map((row) => {
          if (field === "product_type") {
            if (rawValue === "MARGIN" && (row.exchange === "NFO" || row.exchange === "BFO")) {
              return row;
            }
            return { ...row, product_type: rawValue };
          }
          if (field === "transaction_type" || field === "order_type" || field === "validity") {
            return { ...row, [field]: rawValue };
          }
          if (field === "order_price" || field === "trigger_price") {
            const v = rawValue === "" || rawValue == null ? "" : rawValue;
            return { ...row, [field]: v };
          }
          if (field === "disclosed_quantity") {
            if (row.exchange === "NFO") {
              return { ...row, disclosed_quantity: 0 };
            }
            if (rawValue === "" || rawValue == null) {
              return { ...row, disclosed_quantity: "" };
            }
            const num = Number(rawValue) || 0;
            const qty = Number(row.order_quantity ?? row.order_lot) || 0;
            const clamped = Math.min(Math.max(0, num), qty);
            return { ...row, disclosed_quantity: clamped };
          }
          return row;
        })
      );
      bumpModifyBulkRow();
    },
    [bumpModifyBulkRow]
  );

  const bulkApplyModifyNSEQty = useCallback(
    (rawValue) => {
      const qty = Number(rawValue) || 0;
      setModifyFormRows((prev) =>
        prev.map((row) =>
          row.exchange === "NSE"
            ? {
                ...row,
                order_quantity: qty,
                disclosed_quantity: defaultDisclosedForExchangeQty(row.exchange, qty),
              }
            : row
        )
      );
      bumpModifyBulkRow();
    },
    [bumpModifyBulkRow]
  );

  const bulkApplyModifyLot = useCallback(
    (rawValue) => {
      const lotsInt = Math.max(0, Math.floor(Number(rawValue) || 0));
      setModifyFormRows((prev) =>
        prev.map((row) => {
          if (row.exchange === "NSE") return row;
          const qty = lotsInt * (Number(row.lot_size) || 1);
          const disclosed = defaultDisclosedForExchangeQty(row.exchange, qty);
          return { ...row, order_quantity: qty, disclosed_quantity: disclosed };
        })
      );
      bumpModifyBulkRow();
    },
    [bumpModifyBulkRow]
  );

  const bulkApplyInitiateField = useCallback(
    (field, value) => {
      setInitiateFormRows((prev) => prev.map((row) => applyInitiateFieldToRow(row, field, value)));
      bumpInitiateBulkRow();
    },
    [bumpInitiateBulkRow]
  );

  const bulkApplyInitiateNSEQty = useCallback(
    (rawValue) => {
      const qty = Number(rawValue) || 0;
      setInitiateFormRows((prev) =>
        prev.map((row) =>
          row.exchange === "NSE"
            ? {
                ...row,
                order_quantity: qty,
                disclosed_quantity: defaultDisclosedForExchangeQty(row.exchange, qty),
                order_slice_value: syncInitiateSliceWithQty({ ...row, order_quantity: qty }, qty),
              }
            : row
        )
      );
      bumpInitiateBulkRow();
    },
    [bumpInitiateBulkRow]
  );

  const bulkApplyInitiateLot = useCallback(
    (rawValue) => {
      const lotsInt = Math.max(0, Math.floor(Number(rawValue) || 0));
      setInitiateFormRows((prev) =>
        prev.map((row) => {
          if (row.exchange === "NSE") return row;
          const q = lotsInt * (Number(row.lot_size) || 1);
          const disclosed = defaultDisclosedForExchangeQty(row.exchange, q);
          return {
            ...row,
            order_quantity: q,
            disclosed_quantity: disclosed,
            order_slice_value: syncInitiateSliceWithQty({ ...row, order_quantity: q }, q),
          };
        })
      );
      bumpInitiateBulkRow();
    },
    [bumpInitiateBulkRow]
  );

  const handleInitiateNSEQtyChange = useCallback((rowIndex, newQty) => {
    setInitiateFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      if (newQty === "" || newQty == null) {
        next[rowIndex] = { ...next[rowIndex], order_quantity: "", disclosed_quantity: "" };
        return next;
      }
      const qty = Number(newQty) || 0;
      const ex = next[rowIndex].exchange;
      const disclosed = defaultDisclosedForExchangeQty(ex, qty);
      const row = { ...next[rowIndex], order_quantity: qty };
      next[rowIndex] = {
        ...row,
        disclosed_quantity: disclosed,
        order_slice_value: syncInitiateSliceWithQty(row, qty),
      };
      return next;
    });
  }, []);

  const handleInitiateLotChange = useCallback((rowIndex, lots, lotSize) => {
    setInitiateFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      if (lots === "" || lots == null) {
        next[rowIndex] = { ...next[rowIndex], order_quantity: "", disclosed_quantity: "" };
        return next;
      }
      const lotsInt = Math.max(0, Math.floor(Number(lots) || 0));
      const qty = lotsInt * (Number(lotSize) || 1);
      const ex = next[rowIndex].exchange;
      const disclosed = defaultDisclosedForExchangeQty(ex, qty);
      const row = { ...next[rowIndex], order_quantity: qty };
      next[rowIndex] = {
        ...row,
        disclosed_quantity: disclosed,
        order_slice_value: syncInitiateSliceWithQty(row, qty),
      };
      return next;
    });
  }, []);

  const handleModifyFormChange = useCallback((rowIndex, field, value) => {
    setModifyFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      const numericFields = ["order_price", "trigger_price", "disclosed_quantity"];
      const finalValue = numericFields.includes(field)
        ? value === "" || value == null
          ? ""
          : value
        : value;
      next[rowIndex] = { ...next[rowIndex], [field]: finalValue };
      if (next[rowIndex].exchange === "NFO") {
        next[rowIndex] = { ...next[rowIndex], disclosed_quantity: 0 };
      }
      return next;
    });
  }, []);

  const handleModifyNSEQtyChange = useCallback((rowIndex, newQty) => {
    setModifyFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      if (newQty === "" || newQty == null) {
        next[rowIndex] = { ...next[rowIndex], order_quantity: "", disclosed_quantity: "" };
        return next;
      }
      const qty = Number(newQty) || 0;
      const ex = next[rowIndex].exchange;
      const disclosed = defaultDisclosedForExchangeQty(ex, qty);
      next[rowIndex] = { ...next[rowIndex], order_quantity: qty, disclosed_quantity: disclosed };
      return next;
    });
  }, []);

  const handleModifyLotChange = useCallback((rowIndex, lots, lotSize) => {
    setModifyFormRows((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      if (lots === "" || lots == null) {
        next[rowIndex] = { ...next[rowIndex], order_quantity: "", disclosed_quantity: "" };
        return next;
      }
      const lotsInt = Math.max(0, Math.floor(Number(lots) || 0));
      const qty = lotsInt * (Number(lotSize) || 1);
      const ex = next[rowIndex].exchange;
      const disclosed = defaultDisclosedForExchangeQty(ex, qty);
      next[rowIndex] = { ...next[rowIndex], order_quantity: qty, disclosed_quantity: disclosed };
      return next;
    });
  }, []);

  const modifyFormValid = useMemo(() => {
    if (modifyFormRows.length === 0 || !modifyModalSource) return false;
    return modifyFormRows.every((row) => {
      const qty = Number(row.order_quantity ?? row.order_lot) || 0;
      const disc = Number(row.disclosed_quantity) || 0;
      if (disc > qty) return false;
      if (modifyModalSource === "order-book" && !row.broker_order_id) return false;
      return true;
    });
  }, [modifyFormRows, modifyModalSource]);

  const handleConfirmModify = useCallback(async () => {
    if (modifyFormRows.length === 0 || !modifyModalSource) return;
    setIsModifying(true);
    setModifyModalError("");
    setError("");
    setBulkModifyApiSuccessFeedback(null);
    const applyBulkModifyResponse = async (payload) => {
      const { errorRows, successCount } = normalizeBulkOperationResponse(payload);
      try {
        const resp = await getBulkOrders();
        setBulkOrderData(sortBulkOrderResponse(resp || {}));
        setLastBulkRefreshAt(Date.now());
      } catch (refreshErr) {
        setModifyModalError(getErrorMessage(refreshErr));
      }
      if (errorRows.length > 0) {
        setBulkApiRowFeedback({ source: "modify", errorRows, successCount });
        return true;
      }
      return false;
    };
    try {
      if (modifyModalSource === "bulk") {
        const order_list = modifyFormRows.map((row) => buildBulkModifyOrderItem(row));
        try {
          const res = await modifyBulkOrders(order_list);
          const data = res.data;
          const status = res.status;
          const hasRowErrors = await applyBulkModifyResponse(data);
          if (hasRowErrors) {
            setError("");
            setModifyModalError("");
            setBulkModifyApiSuccessFeedback(null);
            setIsModifyModalOpen(true);
          } else if (status >= 400) {
            setBulkModifyApiSuccessFeedback(null);
            setModifyModalError(bulkOrderHttpErrorMessage(data, status));
            setError("");
            setIsModifyModalOpen(true);
          } else {
            setBulkApiRowFeedback(null);
            const updatedCount =
              Array.isArray(data?.bulk_orders) && data.bulk_orders.length > 0 ? data.bulk_orders.length : order_list.length;
            setBulkModifyApiSuccessFeedback({ updatedCount });
          }
        } catch (err) {
          const body = parseAxiosBulkOrderErrorBody(err);
          const hadRowErrors = body && (await applyBulkModifyResponse(body));
          if (hadRowErrors) {
            setError("");
            setModifyModalError("");
            setIsModifyModalOpen(true);
          } else {
            const status = err?.response?.status;
            const msg =
              body && status != null
                ? bulkOrderHttpErrorMessage(body, status)
                : getErrorMessage(err);
            setModifyModalError(msg);
            setError("");
            setIsModifyModalOpen(true);
          }
        }
      } else {
        const order_list = modifyFormRows
          .filter((row) => row.broker_order_id)
          .map((row) => {
            const qtyRaw = row.order_quantity ?? row.order_lot ?? 0;
            const qtyNum = Number(qtyRaw);
            const orderQuantity = Number.isNaN(qtyNum) ? 0 : Math.max(0, Math.floor(qtyNum));
            const disclosedRaw = row.disclosed_quantity ?? 0;
            const disclosedNum = Number(disclosedRaw);
            const disclosedQuantity =
              row.exchange === "NFO"
                ? 0
                : Number.isNaN(disclosedNum)
                  ? 0
                  : Math.max(0, Math.floor(disclosedNum));
            return {
              broker_order_id: row.broker_order_id,
              broker_client_code: row.broker_client_code,
              exchange: row.exchange,
              exchange_trading_symbol: row.exchange_trading_symbol,
              transaction_type: row.transaction_type,
              order_type: row.order_type,
              order_price: Number.isNaN(Number(row.order_price)) ? 0 : Number(row.order_price),
              trigger_price: Number.isNaN(Number(row.trigger_price)) ? 0 : Number(row.trigger_price),
              order_quantity: orderQuantity,
              disclosed_quantity: disclosedQuantity,
              product_type: row.product_type,
            };
          });
        if (order_list.length === 0) {
          setModifyModalError("Missing broker order id for one or more selected rows.");
          return;
        }
        const response = await modifyGatewayOrders(order_list);
        if (response?.status === true) {
          setIsModifyModalOpen(false);
          setModifyModalSource(null);
          setModifyModalError("");
          setSelectedRows([]);
          void fetchOrderBook();
          navigate("/order-status", { state: orderStatusNavigationStateFromResult(response) });
        } else {
          setModifyModalError(response?.detail || "Modification failed.");
        }
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      if (modifyModalSource === "order-book") {
        setModifyModalError(msg);
      } else if (modifyModalSource === "bulk") {
        const body = parseAxiosBulkOrderErrorBody(err);
        if (body && (await applyBulkModifyResponse(body))) {
          setError("");
          setModifyModalError("");
          setIsModifyModalOpen(true);
        } else {
          setModifyModalError(msg);
          setError("");
          setIsModifyModalOpen(true);
        }
      } else {
        setError(msg);
      }
    } finally {
      setIsModifying(false);
    }
  }, [modifyFormRows, modifyModalSource, fetchOrderBook, navigate]);

  /** Table-area banner only — bulk upload/modify errors stay in their modals. */
  const showTableAreaError =
    Boolean(error) && !isUploadModalOpen && !(isModifyModalOpen && modifyModalSource === "bulk");

  return (
    <div className={styles["container"]}>
      <div className={styles["primary-tabs-container"]}>
        <Tabs value={primaryTab} onChange={handlePrimaryTabChange} className={styles["primary-tabs"]}>
          <Tab
            icon={<GlobalIcon iconName="OrderBookIcon" className={styles["tab-icon"]} />}
            iconPosition="start"
            label="Order Book"
            value="order-book"
            className={styles["primary-tab"]}
            disableRipple
          />
          <Tab
            icon={<GlobalIcon iconName="BulkOrderIcon" className={styles["tab-icon"]} />}
            iconPosition="start"
            label="Bulk Order"
            value="bulk-order"
            className={styles["primary-tab"]}
            disableRipple
          />
        </Tabs>
      </div>

      <div className={styles["content-wrapper"]}>
        <div className={styles["tab-content"]} key={primaryTab}>
          {primaryTab === "order-book" && (
            <div className={styles["action-buttons-container"]}>
              {activeTab === "open" && <><Button
                variant="outlined"
                className={styles["action-button"]}
                onClick={handleModify}
                disabled={selectedRows.length === 0}
                startIcon={<GlobalIcon iconName="ModificationBookIcon" />}

              >
                Modify
              </Button>

                <Button
                  variant="outlined"
                  className={styles["action-button"]}
                onClick={handleCancelClick}
                disabled={selectedRows.length === 0}
                  startIcon={<GlobalIcon iconName="RoundedCrossIcon" />}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  className={styles["action-button"]}
                  onClick={handleRepeatClick}
                  disabled={selectedRows.length === 0}
                  startIcon={<GlobalIcon iconName="RefreshIcon" />}
                >
                  Repeat
                </Button></>}

              {activeTab !== "open" &&
                <Button
                  variant="outlined"
                  className={styles["action-button"]}
                  onClick={handleRepeatClick}
                  disabled={selectedRows.length === 0}
                  startIcon={<GlobalIcon iconName="RefreshIcon" />}
                >
                  Repeat
                </Button>
              }
            </div>
          )}

          {primaryTab === "bulk-order" && (
            <div className={styles["action-buttons-container"]}>
              <Button
                variant="contained"
                className={`${styles["action-button"]} ${styles["schedule-button"]}`}
                onClick={handleInitiate}
                disabled={selectedRows.length === 0}
                startIcon={<GlobalIcon iconName="LightbulbIcon" />}
              >
                Initiate
              </Button>
              <Button
                variant="outlined"
                className={styles["action-button"]}
                onClick={handleModify}
                disabled={selectedRows.length === 0}
                startIcon={<GlobalIcon iconName="DoubleLeftArrowIcon" />}
              >
                Modify
              </Button>
              <Button
                variant="outlined"
                className={styles["action-button"]}
                onClick={handleDeleteClick}
                disabled={selectedRows.length === 0}
                startIcon={<GlobalIcon iconName="DeleteTrashIcon" className={styles["action-button-icon-inherit"]} />}
              >
                Delete
              </Button>
              <Button
                variant="outlined"
                className={styles["action-button"]}
                onClick={handleUploadFileClick}
                startIcon={<GlobalIcon iconName="DocumentIcon" />}
              >
                Upload
              </Button>
            </div>
          )}

        </div>

        {showTableAreaError ? (
          <div className={styles["error-message"]}>
            {error}
          </div>
        ) : null}

        <div className={`${styles["table-wrapper"]} ${styles["tab-content"]}`} key={`table-${primaryTab}`}>
          <CustomTable
            columns={currentColumns}
            data={currentTableData}
            onRowSelect={handleRowSelect}
            selectedRows={selectedRows}
            rowKey={currentRowKey}
            enableSearch={true}
            enableSorting={true}
            enableSelection={true}
            enableShowEntries={true}
            enableFilter={false}
            enableExport={true}
            enablePagination={true}
            searchPlaceholder="Search"
            onExport={handleExport}
            exportType={exportType}
            columnsWithDropdown={columnsWithDropdown}
            secondaryTabs={secondaryTabsConfig}
            activeSecondaryTab={activeSecondaryTab}
            onSecondaryTabChange={handleSecondaryTabChange}
            toolbarRefresh={
              <>
                <BookTableColumnPrefsInfo />
                <BookRefreshBar
                  lastRefreshAt={bookLastRefreshAt}
                  onReload={handleBookToolbarReload}
                  isLoading={isLoading}
                />
              </>
            }
          />
          {showBlockingTableLoad ? <div className={styles["loading-message"]}>Loading...</div> : null}
        </div>
      </div>

      {primaryTab === "order-book" && (
        <Dialog
          open={isOrderBookCancelModalOpen}
          onClose={handleOrderBookCancelModalClose}
          maxWidth="sm"
          fullWidth
          scroll="paper"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 220 }}
          PaperProps={{ className: styles["cancel-open-orders-modal-paper"] }}
        >
          <div className={styles["cancel-open-orders-modal-inner"]}>
            {isCancellingOrders && (
              <div className={styles["cancel-open-orders-modal-loader"]}>
                <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
                <span className={styles["cancel-open-orders-loader-text"]}>Cancelling orders…</span>
              </div>
            )}
            <div className={styles["cancel-open-orders-icon-wrap"]}>
              <GlobalIcon iconName="RoundedCrossIcon" className={styles["cancel-open-orders-icon"]} />
            </div>
            <DialogTitle className={styles["cancel-open-orders-title"]} component="div">
              Cancel selected open orders?
            </DialogTitle>
            <DialogContent className={styles["cancel-open-orders-content"]}>
              {orderBookCancelError ? <p className={styles["cancel-open-orders-error"]}>{orderBookCancelError}</p> : null}
              {orderBookCancelCandidates.length > 0 ? (
                <>
                  <p className={styles["cancel-open-orders-lead"]}>
                    You are about to request cancellation for{" "}
                    <strong>
                      {orderBookCancelCandidates.length} open order{orderBookCancelCandidates.length !== 1 ? "s" : ""}
                    </strong>{" "}
                    at the broker. Open positions are not closed by this action—only the listed working orders are withdrawn.
                  </p>
                  <p className={styles["cancel-open-orders-sub"]}>Orders included in this request:</p>
                  <ul className={styles["cancel-open-orders-symbol-list"]}>
                    {orderBookCancelCandidates.slice(0, 8).map((row) => (
                      <li key={row.key ?? row.broker_order_id}>
                        <span className={styles["cancel-open-orders-symbol"]}>{row.exchange_trading_symbol ?? "—"}</span>
                        <span className={styles["cancel-open-orders-meta"]}>
                          <span className={styles["cancel-open-orders-order-id"]}>
                            Order ID: {row.broker_order_id ?? "—"}
                          </span>
                          <span className={styles["cancel-open-orders-qty-price"]}>
                            Qty: {formatCancelModalOrderQty(row)} · Price: {formatCancelModalOrderPrice(row.order_price ?? row.limit_price)}
                          </span>
                          {(row.broker_client_code || row.exchange) && (
                            <span className={styles["cancel-open-orders-meta-secondary"]}>
                              {row.broker_client_code ?? ""}
                              {row.broker_client_code && row.exchange ? " · " : ""}
                              {row.exchange ?? ""}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {orderBookCancelCandidates.length > 8 ? (
                    <p className={styles["cancel-open-orders-more"]}>
                      + {orderBookCancelCandidates.length - 8} more
                    </p>
                  ) : null}
                </>
              ) : (
                <p className={styles["cancel-open-orders-lead"]}>
                  None of the selected rows can be cancelled. Make sure you are on <strong>Open orders</strong>, rows are
                  still open, and each has a broker order id and client code.
                </p>
              )}
              {selectedRows.length > 0 && selectedRows.length > orderBookCancelCandidates.length ? (
                <p className={styles["cancel-open-orders-skip-note"]}>
                  {selectedRows.length - orderBookCancelCandidates.length} selected row
                  {selectedRows.length - orderBookCancelCandidates.length !== 1 ? "s are" : " is"} not included (not open or
                  incomplete data).
                </p>
              ) : null}
              <p className={styles["cancel-open-orders-warning"]}>This cannot be undone from here. Confirm only if you intend to cancel these orders.</p>
            </DialogContent>
            <DialogActions className={styles["cancel-open-orders-actions"]}>
              <Button variant="outlined" onClick={handleOrderBookCancelModalClose} disabled={isCancellingOrders} className={styles["modal-footer-btn"]}>
                Keep orders
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmOrderBookCancel}
                disabled={orderBookCancelCandidates.length === 0 || isCancellingOrders}
                className={styles["schedule-button"]}
              >
                {isCancellingOrders ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Cancel orders"}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      {primaryTab === "order-book" && (
        <Dialog
          open={isOrderBookRepeatModalOpen}
          onClose={handleOrderBookRepeatModalClose}
          maxWidth="sm"
          fullWidth
          scroll="paper"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 220 }}
          PaperProps={{ className: styles["cancel-open-orders-modal-paper"] }}
        >
          <div className={styles["cancel-open-orders-modal-inner"]}>
            {isRepeatingOrders && (
              <div className={styles["cancel-open-orders-modal-loader"]}>
                <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
                <span className={styles["cancel-open-orders-loader-text"]}>Repeating orders…</span>
              </div>
            )}
            <div className={styles["cancel-open-orders-icon-wrap"]}>
              <GlobalIcon iconName="RefreshIcon" className={styles["cancel-open-orders-icon"]} />
            </div>
            <DialogTitle className={styles["cancel-open-orders-title"]} component="div">
              Repeat selected orders?
            </DialogTitle>
            <DialogContent className={styles["cancel-open-orders-content"]}>
              {orderBookRepeatError ? <p className={styles["cancel-open-orders-error"]}>{orderBookRepeatError}</p> : null}
              {orderBookRepeatCandidates.length > 0 ? (
                <>
                  <p className={styles["cancel-open-orders-lead"]}>
                    The broker will place <strong>{orderBookRepeatCandidates.length}</strong> new order
                    {orderBookRepeatCandidates.length !== 1 ? "s" : ""} based on the selection below (same symbol, side, and
                    parameters per broker rules).
                  </p>
                  <p className={styles["cancel-open-orders-sub"]}>Orders to repeat:</p>
                  <ul className={styles["cancel-open-orders-symbol-list"]}>
                    {orderBookRepeatCandidates.map((row, idx) => (
                      <li key={`${row.key ?? row.broker_order_id}-${idx}`}>
                        <span className={styles["cancel-open-orders-symbol"]}>{row.exchange_trading_symbol ?? "—"}</span>
                        <span className={styles["cancel-open-orders-meta"]}>
                          <span className={styles["cancel-open-orders-order-id"]}>
                            Order ID: {row.broker_order_id ?? "—"}
                          </span>
                          <span className={styles["cancel-open-orders-qty-price"]}>
                            Qty: {formatCancelModalOrderQty(row)} · Price: {formatCancelModalOrderPrice(row.order_price ?? row.limit_price)}
                          </span>
                          {(row.broker_client_code || row.exchange) && (
                            <span className={styles["cancel-open-orders-meta-secondary"]}>
                              {row.broker_client_code ?? ""}
                              {row.broker_client_code && row.exchange ? " · " : ""}
                              {row.exchange ?? ""}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className={styles["cancel-open-orders-lead"]}>
                  Nothing to repeat for the current selection. Each row needs a broker order id and client code.
                </p>
              )}
              {selectedRows.length > orderBookRepeatCandidates.length ? (
                <p className={styles["cancel-open-orders-skip-note"]}>
                  {selectedRows.length - orderBookRepeatCandidates.length} selected row
                  {selectedRows.length - orderBookRepeatCandidates.length !== 1 ? "s are" : " is"} not included (incomplete
                  data).
                </p>
              ) : null}
              <p className={styles["cancel-open-orders-warning"]}>
                This submits new orders to the broker. Confirm only if you intend to repeat these orders.
              </p>
            </DialogContent>
            <DialogActions className={styles["cancel-open-orders-actions"]}>
              <Button variant="outlined" onClick={handleOrderBookRepeatModalClose} disabled={isRepeatingOrders} className={styles["modal-footer-btn"]}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmOrderBookRepeat}
                disabled={orderBookRepeatCandidates.length === 0 || isRepeatingOrders}
                className={styles["schedule-button"]}
              >
                {isRepeatingOrders ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Repeat orders"}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      {primaryTab === "bulk-order" && (
        <Dialog
          open={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 220 }}
          PaperProps={{ className: styles["delete-modal-paper"] }}
        >
          <div className={styles["delete-modal-content"]}>
            <div className={styles["delete-modal-icon-wrap"]}>
              <GlobalIcon iconName="DeleteTrashIcon" className={styles["delete-modal-icon"]} />
            </div>
            <DialogTitle className={styles["delete-modal-title"]}>Delete bulk order(s)?</DialogTitle>
            <p className={styles["delete-modal-message"]}>
              Are you sure you want to delete the selected {selectedRows.length} item{selectedRows.length !== 1 ? "s" : ""}? This action cannot be undone.
            </p>
            <DialogActions className={styles["delete-modal-actions"]}>
              <Button variant="outlined" onClick={handleDeleteModalClose} disabled={isDeleting} className={styles["modal-footer-btn"]}>
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isDeleting} className={styles["modal-footer-btn"]}>
                {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      {primaryTab === "bulk-order" && (
        <Dialog
          open={isUploadModalOpen}
          onClose={handleUploadModalClose}
          maxWidth="md"
          fullWidth
          scroll="paper"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 280 }}
          PaperProps={{ className: styles["upload-modal-paper"] }}
        >
          <div className={styles["upload-modal-inner"]}>
            {isUploading && (
              <div className={styles["upload-modal-loader"]}>
                <div className={styles["upload-loader-spinner"]} />
                <span className={styles["upload-loader-text"]}>Uploading...</span>
              </div>
            )}
            <DialogTitle className={styles["upload-modal-title"]}>Upload bulk order file</DialogTitle>
            <DialogContent className={styles["upload-modal-content"]}>
              {uploadModalError ? (
                <p className={styles["upload-modal-error"]} role="alert">
                  {uploadModalError}
                </p>
              ) : null}
              <p className={styles["upload-modal-description"]}>
                Upload an Excel or CSV file with your bulk order data.
              </p>
              <div className={styles["upload-download-sample-wrap"]}>
                <Button
                  variant="outlined"
                  className={styles["upload-download-sample-btn"]}
                  onClick={handleDownloadSample}
                  disabled={isUploading || isDownloadingSample}
                  startIcon={isDownloadingSample ? <CircularProgress size={16} color="inherit" /> : <GlobalIcon iconName="DownloadIcon" />}
                >
                  {isDownloadingSample ? "Downloading..." : "Download sample file"}
                </Button>
                {isDownloadingSample && (
                  <LinearProgress className={styles["upload-sample-progress"]} color="primary" />
                )}
              </div>
              <div
                className={styles["upload-drop-zone"]}
                onDrop={handleUploadModalDrop}
                onDragOver={handleUploadModalDragOver}
                onClick={() => uploadModalInputRef.current?.click()}
              >
                <input
                  ref={uploadModalInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleUploadModalFileChange}
                  className={styles["upload-file-input"]}
                  aria-hidden="true"
                />
                <GlobalIcon iconName="DocumentIcon" className={styles["upload-drop-icon"]} />
                <span className={styles["upload-drop-text"]}>
                  {uploadModalFile
                    ? uploadModalFile.name
                    : "Drag & drop your file here or click to browse"}
                </span>
              </div>
              {bulkApiRowFeedback?.source === "upload" && bulkApiRowFeedback.errorRows?.length > 0 ? (
                <BulkApiRowErrorsPanel
                  feedback={bulkApiRowFeedback}
                  onDismiss={() => setBulkApiRowFeedback(null)}
                />
              ) : null}
            </DialogContent>
            <DialogActions className={styles["upload-modal-actions"]}>
              <Button variant="outlined" onClick={handleUploadModalClose} disabled={isUploading} className={styles["modal-footer-btn"]}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleUploadSubmit} disabled={!uploadModalFile || isUploading} className={styles["schedule-button"]}>
                Upload
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      {(primaryTab === "bulk-order" || primaryTab === "order-book") && (
        <Dialog
          open={isModifyModalOpen}
          onClose={() => !isModifying && closeModifyModal()}
          maxWidth="lg"
          fullWidth
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 220 }}
          PaperProps={{ className: styles["modify-modal-paper"] }}
        >
          <div className={styles["modify-modal-inner"]}>
            {isModifying && (
              <div className={styles["modify-modal-loader"]}>
                <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
                <span className={styles["modify-loader-text"]}>Updating orders...</span>
              </div>
            )}
            <DialogTitle className={styles["modify-modal-title"]}>
              {modifyModalSource === "order-book" ? "Modify open orders" : "Modify bulk orders"}
            </DialogTitle>
            <DialogContent className={styles["modify-modal-content"]}>
              {modifyModalSource === "bulk" && bulkModifyApiSuccessFeedback ? (
                <BulkApiBulkModifySuccessPanel updatedCount={bulkModifyApiSuccessFeedback.updatedCount} />
              ) : (
                <>
                  {modifyModalError ? <p className={styles["modify-modal-error"]}>{modifyModalError}</p> : null}
                  {bulkApiRowFeedback?.source === "modify" && bulkApiRowFeedback.errorRows?.length > 0 ? (
                    <BulkApiRowErrorsPanel
                      feedback={bulkApiRowFeedback}
                      onDismiss={() => setBulkApiRowFeedback(null)}
                    />
                  ) : null}
                  <p className={styles["modify-modal-description"]}>
                    {modifyModalSource === "order-book" ? (
                      <>Edit the selected open orders below. Changes are sent to the broker when you confirm.</>
                    ) : (
                      <>
                        Edit per row below, or use the <strong>Apply to all</strong> row to set one value for every selected order in that column (dropdowns apply on change; numbers apply when you leave the field).
                      </>
                    )}
                  </p>
                  {modifyFormRows.length === 0 ? (
                    <p className={styles["modify-modal-empty"]}>No orders selected.</p>
                  ) : (
                    <div className={styles["modify-table-wrap"]}>
                  <Table size="small" className={styles["modify-table"]}>
                    <TableHead>
                      <TableRow className={styles["modify-header-row"]}>
                        <TableCell className={styles["modify-th"]}>Client</TableCell>
                        <TableCell className={styles["modify-th"]}>Symbol</TableCell>
                        <TableCell className={styles["modify-th"]}>Exchange</TableCell>
                        <TableCell className={styles["modify-th"]}>Trans</TableCell>
                        <TableCell className={styles["modify-th"]}>Order type</TableCell>
                        <TableCell className={styles["modify-th"]}>Price</TableCell>
                        <TableCell className={`${styles["modify-th"]} ${styles["modify-th-lot"]}`}>Lot</TableCell>
                        <TableCell className={`${styles["modify-th"]} ${styles["modify-th-qty"]}`}>Qty</TableCell>
                        <TableCell className={`${styles["modify-th"]} ${styles["modify-th-disclosed"]}`}>Disclosed</TableCell>
                        <TableCell className={`${styles["modify-th"]} ${styles["modify-th-product"]}`}>Product</TableCell>
                        {modifyModalSource === "bulk" ? <TableCell className={styles["modify-th"]}>Validity</TableCell> : null}
                      </TableRow>
                      <TableRow key="modify-apply-to-all" className={styles["modify-bulk-row"]}>
                        <TableCell className={styles["modify-td-bulk"]} title="Set this column for every selected row">
                          <div className={styles["bulk-row-label"]}>
                            <span className={styles["bulk-row-label-title"]}>Apply to all</span>
                          </div>
                        </TableCell>
                        <TableCell className={styles["modify-td-bulk"]} />
                        <TableCell className={styles["modify-td-bulk"]} />
                        <TableCell className={styles["modify-td-bulk"]}>
                          <Select
                            size="small"
                            defaultValue=""
                            displayEmpty
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) bulkApplyModifyField("transaction_type", v);
                            }}
                            className={styles["modify-select"]}
                            MenuProps={MODIFY_SELECT_MENU_PROPS}
                            renderValue={(v) => (v ? v : "—")}
                          >
                            <MenuItem value="" disabled>
                              Trans
                            </MenuItem>
                            <MenuItem value="BUY">BUY</MenuItem>
                            <MenuItem value="SELL">SELL</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell className={styles["modify-td-bulk"]}>
                          <Select
                            size="small"
                            defaultValue=""
                            displayEmpty
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) bulkApplyModifyField("order_type", v);
                            }}
                            className={`${styles["modify-select"]} ${styles["modify-order-type-select"]}`}
                            MenuProps={MODIFY_SELECT_MENU_PROPS}
                            renderValue={(v) => (v ? v : "—")}
                          >
                            <MenuItem value="" disabled>
                              Type
                            </MenuItem>
                            <MenuItem value="LIMIT">LIMIT</MenuItem>
                            <MenuItem value="BID_ASK">Bid-Ask</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell className={styles["modify-td-bulk"]}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Price"
                            defaultValue=""
                            className={styles["modify-input"]}
                            inputProps={{ min: 0, step: 0.01 }}
                            onChange={(e) => {
                              const val = e.target.value;
                              scheduleBulkApplyTimer("m-price", () => {
                                const v = val.trim();
                                if (v !== "") bulkApplyModifyField("order_price", v);
                              });
                            }}
                            onBlur={(e) => {
                              cancelBulkApplyTimer("m-price");
                              const v = e.target.value?.trim();
                              if (v !== "") bulkApplyModifyField("order_price", v);
                            }}
                          />
                        </TableCell>
                        <TableCell className={`${styles["modify-td-bulk"]} ${styles["modify-td-lot"]}`}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Lots"
                            title="Non-NSE rows only"
                            defaultValue=""
                            className={styles["modify-input"]}
                            inputProps={{ min: 0, step: 1 }}
                            onChange={(e) => {
                              const val = e.target.value;
                              scheduleBulkApplyTimer("m-lot", () => {
                                const v = val.trim();
                                if (v !== "") bulkApplyModifyLot(v);
                              });
                            }}
                            onBlur={(e) => {
                              cancelBulkApplyTimer("m-lot");
                              const v = e.target.value?.trim();
                              if (v !== "") bulkApplyModifyLot(v);
                            }}
                          />
                        </TableCell>
                        <TableCell className={`${styles["modify-td-bulk"]} ${styles["modify-td-qty"]}`}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Qty"
                            title="NSE rows only"
                            defaultValue=""
                            className={styles["modify-input"]}
                            inputProps={{ min: 0 }}
                            onChange={(e) => {
                              const val = e.target.value;
                              scheduleBulkApplyTimer("m-nse", () => {
                                const v = val.trim();
                                if (v !== "") bulkApplyModifyNSEQty(v);
                              });
                            }}
                            onBlur={(e) => {
                              cancelBulkApplyTimer("m-nse");
                              const v = e.target.value?.trim();
                              if (v !== "") bulkApplyModifyNSEQty(v);
                            }}
                          />
                        </TableCell>
                        <TableCell className={`${styles["modify-td-bulk"]} ${styles["modify-td-disclosed"]}`}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Disclosed"
                            defaultValue=""
                            className={styles["modify-input"]}
                            inputProps={{ min: 0 }}
                            onChange={(e) => {
                              const val = e.target.value;
                              scheduleBulkApplyTimer("m-disc", () => {
                                const v = val.trim();
                                if (v !== "") bulkApplyModifyField("disclosed_quantity", v);
                              });
                            }}
                            onBlur={(e) => {
                              cancelBulkApplyTimer("m-disc");
                              const v = e.target.value?.trim();
                              if (v !== "") bulkApplyModifyField("disclosed_quantity", v);
                            }}
                          />
                        </TableCell>
                        <TableCell className={`${styles["modify-td-bulk"]} ${styles["modify-td-product"]}`}>
                          <Select
                            size="small"
                            defaultValue=""
                            displayEmpty
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) bulkApplyModifyField("product_type", v);
                            }}
                            className={styles["modify-select"]}
                            MenuProps={MODIFY_SELECT_MENU_PROPS}
                            renderValue={(v) => (v ? v : "—")}
                          >
                            <MenuItem value="" disabled>
                              Product
                            </MenuItem>
                            <MenuItem value="DELIVERY">DELIVERY</MenuItem>
                            <MenuItem value="INTRADAY">INTRADAY</MenuItem>
                            <MenuItem value="MARGIN">MARGIN</MenuItem>
                          </Select>
                        </TableCell>
                        {modifyModalSource === "bulk" ? (
                          <TableCell className={styles["modify-td-bulk"]}>
                            <Select
                              size="small"
                              defaultValue=""
                              displayEmpty
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) bulkApplyModifyField("validity", v);
                              }}
                              className={styles["modify-select"]}
                              MenuProps={MODIFY_SELECT_MENU_PROPS}
                              renderValue={(v) => (v ? v : "—")}
                            >
                              <MenuItem value="" disabled>
                                Validity
                              </MenuItem>
                              <MenuItem value="IOC">IOC</MenuItem>
                              <MenuItem value="DAY">DAY</MenuItem>
                            </Select>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modifyFormRows.map((row, idx) => (
                        <MemoOrderBookModifyBulkFormRow
                          key={`${row.bulk_order_id ?? row.orderBookRowKey ?? `modify-${idx}`}-rev-${modifyBulkRowKey}`}
                          rowIndex={idx}
                          row={row}
                          modifyModalSource={modifyModalSource}
                          styles={styles}
                          modifySelectMenuProps={MODIFY_SELECT_MENU_PROPS}
                          onFieldChange={handleModifyFormChange}
                          onLotChange={handleModifyLotChange}
                          onNSEQtyChange={handleModifyNSEQtyChange}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions className={styles["modify-modal-actions"]}>
              {modifyModalSource === "bulk" && bulkModifyApiSuccessFeedback ? (
                <Button variant="contained" color="success" onClick={dismissBulkModifySuccess} className={styles["schedule-button"]}>
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outlined" onClick={closeModifyModal} disabled={isModifying} className={styles["modal-footer-btn"]}>
                    Cancel
                  </Button>
                  <Button variant="contained" color="primary" onClick={handleConfirmModify} disabled={!modifyFormValid || isModifying} className={styles["schedule-button"]}>
                    Modify
                  </Button>
                </>
              )}
            </DialogActions>
          </div>
        </Dialog>
      )}

      {primaryTab === "bulk-order" && (
        <Dialog
          open={isInitiateModalOpen}
          onClose={() => !isPlacing && setIsInitiateModalOpen(false)}
          maxWidth="lg"
          fullWidth
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 240 }}
          PaperProps={{ className: styles["initiate-modal-paper"] }}
        >
          <div className={styles["initiate-modal-inner"]}>
            {isPlacing && (
              <div className={styles["initiate-modal-loader"]}>
                <CircularProgress size={44} sx={{ color: "var(--accent-hover)" }} thickness={4} />
                <span className={styles["initiate-loader-text"]}>Placing orders...</span>
              </div>
            )}
            <div className={styles["initiate-modal-header"]}>
              <DialogTitle className={styles["initiate-modal-title"]} disableTypography>Initiate order</DialogTitle>
              <p className={styles["initiate-modal-subtitle"]}>
                Review and confirm to place the selected orders. Use the highlighted <strong>Apply to all</strong> row to fill a whole column at once.
              </p>
            </div>
            <DialogContent className={styles["initiate-modal-content"]}>
              {selectedRowsData.length === 0 ? (
                <div className={styles["initiate-modal-empty"]}>
                  <GlobalIcon iconName="DocumentIcon" className={styles["initiate-empty-icon"]} />
                  <p>No orders selected.</p>
                  <span>Select rows from the bulk order table and try again.</span>
                </div>
              ) : (
                <>
                  <div className={styles["initiate-summary-bar"]}>
                    <span className={styles["initiate-summary-count"]}>
                      {selectedRowsData.length} order{selectedRowsData.length !== 1 ? "s" : ""} selected
                    </span>
                  </div>
                  <div className={styles["initiate-table-wrap"]}>
                    <Table size="small" className={styles["initiate-modal-table"]}>
                      <TableHead>
                        <TableRow className={styles["initiate-header-row"]}>
                          <TableCell className={styles["initiate-th"]}>Client</TableCell>
                          <TableCell className={styles["initiate-th"]}>Symbol</TableCell>
                          <TableCell className={styles["initiate-th"]}>Exchange</TableCell>
                          <TableCell className={styles["initiate-th"]}>Trans</TableCell>
                          <TableCell className={styles["initiate-th"]}>Order type</TableCell>
                          <TableCell className={styles["initiate-th"]}>Price</TableCell>
                          <TableCell className={styles["initiate-th"]}>Lot</TableCell>
                          <TableCell className={styles["initiate-th"]}>Qty</TableCell>
                          <TableCell className={styles["initiate-th"]}>Disclosed</TableCell>
                          <TableCell className={styles["initiate-th"]}>Product</TableCell>
                          <TableCell className={styles["initiate-th"]}>Validity</TableCell>
                        </TableRow>
                        <TableRow key="initiate-apply-to-all" className={styles["initiate-bulk-row"]}>
                          <TableCell className={styles["initiate-td-bulk"]} title="Set this column for every selected row">
                            <div className={styles["bulk-row-label"]}>
                              <span className={styles["bulk-row-label-title"]}>Apply to all</span>
                            </div>
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]} />
                          <TableCell className={styles["initiate-td-bulk"]} />
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <Select
                              size="small"
                              defaultValue=""
                              displayEmpty
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) bulkApplyInitiateField("transaction_type", v);
                              }}
                              className={styles["modify-select"]}
                              MenuProps={MODIFY_SELECT_MENU_PROPS}
                              renderValue={(v) => (v ? v : "—")}
                            >
                              <MenuItem value="" disabled>
                                Trans
                              </MenuItem>
                              <MenuItem value="BUY">BUY</MenuItem>
                              <MenuItem value="SELL">SELL</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <Select
                              size="small"
                              defaultValue=""
                              displayEmpty
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) bulkApplyInitiateField("order_type", v);
                              }}
                              className={`${styles["modify-select"]} ${styles["modify-order-type-select"]}`}
                              MenuProps={MODIFY_SELECT_MENU_PROPS}
                              renderValue={(v) => (v ? v : "—")}
                            >
                              <MenuItem value="" disabled>
                                Type
                              </MenuItem>
                              <MenuItem value="LIMIT">LIMIT</MenuItem>
                              <MenuItem value="BID_ASK">Bid-Ask</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Price"
                              defaultValue=""
                              className={styles["modify-input"]}
                              inputProps={{ min: 0, step: 0.01 }}
                              onChange={(e) => {
                                const val = e.target.value;
                                scheduleBulkApplyTimer("i-price", () => {
                                  const v = val.trim();
                                  if (v !== "") bulkApplyInitiateField("order_price", Number(v) || 0);
                                });
                              }}
                              onBlur={(e) => {
                                cancelBulkApplyTimer("i-price");
                                const v = e.target.value?.trim();
                                if (v !== "") bulkApplyInitiateField("order_price", Number(v) || 0);
                              }}
                            />
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Lots"
                              title="Non-NSE rows only"
                              defaultValue=""
                              className={styles["modify-input"]}
                              inputProps={{ min: 0, step: 1 }}
                              onChange={(e) => {
                                const val = e.target.value;
                                scheduleBulkApplyTimer("i-lot", () => {
                                  const v = val.trim();
                                  if (v !== "") bulkApplyInitiateLot(v);
                                });
                              }}
                              onBlur={(e) => {
                                cancelBulkApplyTimer("i-lot");
                                const v = e.target.value?.trim();
                                if (v !== "") bulkApplyInitiateLot(v);
                              }}
                            />
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Qty"
                              title="NSE rows only"
                              defaultValue=""
                              className={styles["modify-input"]}
                              inputProps={{ min: 0 }}
                              onChange={(e) => {
                                const val = e.target.value;
                                scheduleBulkApplyTimer("i-nse", () => {
                                  const v = val.trim();
                                  if (v !== "") bulkApplyInitiateNSEQty(v);
                                });
                              }}
                              onBlur={(e) => {
                                cancelBulkApplyTimer("i-nse");
                                const v = e.target.value?.trim();
                                if (v !== "") bulkApplyInitiateNSEQty(v);
                              }}
                            />
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Disclosed"
                              defaultValue=""
                              className={styles["modify-input"]}
                              inputProps={{ min: 0 }}
                              onChange={(e) => {
                                const val = e.target.value;
                                scheduleBulkApplyTimer("i-disc", () => {
                                  const v = val.trim();
                                  if (v !== "") bulkApplyInitiateField("disclosed_quantity", v);
                                });
                              }}
                              onBlur={(e) => {
                                cancelBulkApplyTimer("i-disc");
                                const v = e.target.value?.trim();
                                if (v !== "") bulkApplyInitiateField("disclosed_quantity", v);
                              }}
                            />
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <Select
                              size="small"
                              defaultValue=""
                              displayEmpty
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) bulkApplyInitiateField("product_type", v);
                              }}
                              className={styles["modify-select"]}
                              MenuProps={MODIFY_SELECT_MENU_PROPS}
                              renderValue={(v) => (v ? v : "—")}
                            >
                              <MenuItem value="" disabled>
                                Product
                              </MenuItem>
                              <MenuItem value="DELIVERY">DELIVERY</MenuItem>
                              <MenuItem value="INTRADAY">INTRADAY</MenuItem>
                              <MenuItem value="MARGIN">MARGIN</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell className={styles["initiate-td-bulk"]}>
                            <Select
                              size="small"
                              defaultValue=""
                              displayEmpty
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) bulkApplyInitiateField("validity", v);
                              }}
                              className={styles["modify-select"]}
                              MenuProps={MODIFY_SELECT_MENU_PROPS}
                              renderValue={(v) => (v ? v : "—")}
                            >
                              <MenuItem value="" disabled>
                                Validity
                              </MenuItem>
                              <MenuItem value="IOC">IOC</MenuItem>
                              <MenuItem value="DAY">DAY</MenuItem>
                            </Select>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {initiateFormRows.map((row, index) => (
                          <MemoOrderBookInitiateBulkFormRow
                            key={`${row.bulk_order_id ?? `init-${index}`}-rev-${initiateBulkRowKey}`}
                            rowIndex={index}
                            row={row}
                            styles={styles}
                            modifySelectMenuProps={MODIFY_SELECT_MENU_PROPS}
                            onFieldChange={handleInitiateFormChange}
                            onLotChange={handleInitiateLotChange}
                            onNSEQtyChange={handleInitiateNSEQtyChange}
                            alternateRow={index % 2 === 1}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
              {error && <div className={styles["initiate-modal-error"]}>{error}</div>}
            </DialogContent>
            <DialogActions className={styles["initiate-modal-actions"]}>
              <Button variant="outlined" onClick={() => setIsInitiateModalOpen(false)} disabled={isPlacing} className={styles["modal-footer-btn"]}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleConfirmInitiate} disabled={!canPlaceOrders || isPlacing} className={styles["schedule-button"]}>
                Confirm & place
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default OrderBookPage;
